import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState, useMemo } from "react";
import LoadingButton from "../../components/LoadingButton";
import { Company } from "../../types";
import { BRAND_VOICE_FIELD_LIMITS } from "../../constants";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`brand-voice-tabpanel-${index}`}
      aria-labelledby={`brand-voice-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

const defaultFormData = {
  name: "",
  industry: "",
  audience: "",
  description: "",
  // Brand personality
  brandPersonality: {
    traits: [],
    tone: "",
    character: "",
  },
  // Communication style
  communicationStyle: {
    formality: "",
    complexity: "",
    style: [],
  },
  // Language guidelines
  languageGuidelines: {
    vocabulary: [],
    phrases: [],
    terminology: [],
  },
  // Content structure
  contentStructure: {
    formats: [],
    organization: [],
    preferences: [],
  },
  // Visual elements
  visualElements: {
    imagery: [],
    colors: [],
    typography: "",
  },
  // Distinctive features
  distinctiveFeatures: {
    unique_elements: [], // Keep snake_case for database compatibility
    taglines: [],
    motifs: [],
  },
  // Personal narrative fields for authentic, story-driven content
  personalBackground: {
    bio: "",
    originStory: "",
    careerJourney: [],
    formativeExperiences: [],
    personalValues: [],
  },
  achievements: {
    careerWins: [],
    metrics: [],
    recognition: [],
    proudMoments: [],
  },
  failures: {
    setbacks: [],
    lessonsLearned: [],
    pivots: [],
    vulnerabilityTopics: [],
  },
  // Editing instructions for content polishing
  editingInstructions: "",
};

interface BrandVoiceFormProps {
  open: boolean;
  onClose: () => void;
  companyId?: string;
  company?: Company;
  onSave?: (formData: any) => void;
  existingData?: any;
  loading?: boolean;
}

export default ({
  open,
  onClose,
  companyId,
  company,
  onSave,
  existingData,
  loading = false,
}: BrandVoiceFormProps) => {
  const [tabValue, setTabValue] = useState(0);

  // Initialize with default data, the useEffect will update it when existingData changes
  const [formData, setFormData] = useState<any>(defaultFormData);

  useEffect(() => {
    // When existingData changes, update the form state
    if (existingData) {
      // Create a new form data object with proper defaults for any missing fields
      const updatedFormData = {
        ...defaultFormData,
        ...existingData,
      };
      setFormData(updatedFormData);
    }
  }, [existingData]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSubmit = () => {
    const newBrandVoice = {
      id: existingData?.id || null,
      companyId: companyId,
      ...formData,
    };

    if (onSave) {
      onSave(newBrandVoice);
    }

    onClose();
  };

  const handleInputChange = (event: any) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleNestedChange = (section: string, field: string, value: any) => {
    setFormData({
      ...formData,
      [section]: {
        ...formData[section],
        [field]: value,
      },
    });
  };

  const handleArrayInput = (section: string, field: string, event: any) => {
    // Convert comma-separated string to array, handling empty/null values
    const value = event.target.value
      ? event.target.value
          .split(",")
          .map((item: string) => item.trim())
          .filter((item: string) => item) // Remove empty items
      : [];
    handleNestedChange(section, field, value);
  };

  // Validation errors
  const validationErrors = useMemo(() => {
    const errors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      errors.name = "Name is required";
    } else if (formData.name.length > BRAND_VOICE_FIELD_LIMITS.name) {
      errors.name = `Name must be ${BRAND_VOICE_FIELD_LIMITS.name} characters or less`;
    }

    if (formData.industry && formData.industry.length > BRAND_VOICE_FIELD_LIMITS.industry) {
      errors.industry = `Industry must be ${BRAND_VOICE_FIELD_LIMITS.industry} characters or less`;
    }

    if (formData.audience && formData.audience.length > BRAND_VOICE_FIELD_LIMITS.audience) {
      errors.audience = `Target Audience must be ${BRAND_VOICE_FIELD_LIMITS.audience} characters or less`;
    }

    if (formData.description && formData.description.length > BRAND_VOICE_FIELD_LIMITS.description) {
      errors.description = `Description must be ${BRAND_VOICE_FIELD_LIMITS.description} characters or less`;
    }

    return errors;
  }, [formData.name, formData.industry, formData.audience, formData.description]);

  const isFormValid = Object.keys(validationErrors).length === 0 && formData.name?.trim();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {existingData ? "Edit" : "New"} Brand Voice
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="brand voice tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab label="Basic Info" />
            <Tab label="Brand Personality" />
            <Tab label="Communication & Language" />
            <Tab label="Content Structure" />
            <Tab label="Visual & Distinctive" />
            <Tab label="Personal Narrative" />
            <Tab label="Editing Instructions" />
          </Tabs>
        </Box>

        {/* Basic Info Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              name="name"
              label="Brand Voice Name"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              required
              error={!!validationErrors.name}
              helperText={
                validationErrors.name ||
                `${formData.name?.length || 0}/${BRAND_VOICE_FIELD_LIMITS.name} characters`
              }
              inputProps={{ maxLength: BRAND_VOICE_FIELD_LIMITS.name }}
            />

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Basic Brand Information
            </Typography>
            <Divider />

            <FormControl fullWidth>
              <InputLabel>Industry</InputLabel>
              <Select
                name="industry"
                value={formData.industry}
                label="Industry"
                onChange={handleInputChange}
              >
                <MenuItem value="Technology">Technology</MenuItem>
                <MenuItem value="Healthcare">Healthcare</MenuItem>
                <MenuItem value="Finance">Finance</MenuItem>
                <MenuItem value="Education">Education</MenuItem>
                <MenuItem value="Retail">Retail</MenuItem>
                <MenuItem value="Entertainment">Entertainment</MenuItem>
                <MenuItem value="Manufacturing">Manufacturing</MenuItem>
                <MenuItem value="Hospitality">Hospitality</MenuItem>
                <MenuItem value="Professional Services">
                  Professional Services
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              name="audience"
              label="Target Audience"
              value={formData.audience}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={3}
              placeholder="e.g., Professionals aged 25-45, Small business owners"
              error={!!validationErrors.audience}
              helperText={
                validationErrors.audience ||
                `${formData.audience?.length || 0}/${BRAND_VOICE_FIELD_LIMITS.audience} characters`
              }
              inputProps={{ maxLength: BRAND_VOICE_FIELD_LIMITS.audience }}
            />

            <TextField
              name="description"
              label="Brand Voice Overview"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={4}
              fullWidth
              placeholder="Provide a brief overview of your brand voice"
              error={!!validationErrors.description}
              helperText={
                validationErrors.description ||
                `${formData.description?.length || 0}/${BRAND_VOICE_FIELD_LIMITS.description} characters`
              }
              inputProps={{ maxLength: BRAND_VOICE_FIELD_LIMITS.description }}
            />
          </Box>
        </TabPanel>

        {/* Brand Personality Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6">Brand Personality</Typography>

            <TextField
              label="Key Brand Traits"
              value={formData.brandPersonality?.traits?.join(", ") || ""}
              onChange={(e) =>
                handleArrayInput("brandPersonality", "traits", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Enter traits separated by commas (e.g., Friendly, Authoritative, Innovative)"
            />

            <FormControl fullWidth>
              <InputLabel>Tone of Voice</InputLabel>
              <Select
                value={formData.brandPersonality?.tone}
                label="Tone of Voice"
                onChange={(e) =>
                  handleNestedChange("brandPersonality", "tone", e.target.value)
                }
              >
                <MenuItem value="Formal">Formal</MenuItem>
                <MenuItem value="Conversational">Conversational</MenuItem>
                <MenuItem value="Professional">Professional</MenuItem>
                <MenuItem value="Playful">Playful</MenuItem>
                <MenuItem value="Authoritative">Authoritative</MenuItem>
                <MenuItem value="Empathetic">Empathetic</MenuItem>
                <MenuItem value="Inspirational">Inspirational</MenuItem>
                <MenuItem value="Humorous">Humorous</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Brand Character Description"
              value={formData.brandPersonality?.character}
              onChange={(e) =>
                handleNestedChange(
                  "brandPersonality",
                  "character",
                  e.target.value
                )
              }
              fullWidth
              multiline
              rows={3}
              placeholder="Describe your brand as if it were a person. What would their personality be like?"
            />
          </Box>
        </TabPanel>

        {/* Communication & Language Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6">Communication Style</Typography>

            <FormControl fullWidth>
              <InputLabel>Level of Formality</InputLabel>
              <Select
                value={formData.communicationStyle?.formality}
                label="Level of Formality"
                onChange={(e) =>
                  handleNestedChange(
                    "communicationStyle",
                    "formality",
                    e.target.value
                  )
                }
              >
                <MenuItem value="Very Formal">Very Formal</MenuItem>
                <MenuItem value="Formal">Formal</MenuItem>
                <MenuItem value="Semi-Formal">Semi-Formal</MenuItem>
                <MenuItem value="Casual">Casual</MenuItem>
                <MenuItem value="Very Casual">Very Casual</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Language Complexity</InputLabel>
              <Select
                value={formData.communicationStyle?.complexity}
                label="Language Complexity"
                onChange={(e) =>
                  handleNestedChange(
                    "communicationStyle",
                    "complexity",
                    e.target.value
                  )
                }
              >
                <MenuItem value="Simple">Simple and accessible</MenuItem>
                <MenuItem value="Moderate">Moderate complexity</MenuItem>
                <MenuItem value="Technical">Technical and specialized</MenuItem>
                <MenuItem value="Academic">Academic or scholarly</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Communication Style Elements"
              value={formData.communicationStyle?.style?.join(", ") || ""}
              onChange={(e) =>
                handleArrayInput("communicationStyle", "style", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Enter style elements separated by commas (e.g., Direct, Storytelling, Instructional)"
            />

            <Typography variant="h6" sx={{ mt: 2 }}>
              Language Guidelines
            </Typography>

            <TextField
              label="Key Vocabulary"
              value={formData.languageGuidelines?.vocabulary?.join(", ") || ""}
              onChange={(e) =>
                handleArrayInput("languageGuidelines", "vocabulary", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Enter key words to use separated by commas"
            />

            <TextField
              label="Brand Phrases & Expressions"
              value={formData.languageGuidelines?.phrases?.join(", ") || ""}
              onChange={(e) =>
                handleArrayInput("languageGuidelines", "phrases", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Enter signature phrases separated by commas"
            />

            <TextField
              label="Industry Terminology"
              value={formData.languageGuidelines?.terminology?.join(", ") || ""}
              onChange={(e) =>
                handleArrayInput("languageGuidelines", "terminology", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Enter specific technical terms or jargon separated by commas"
            />
          </Box>
        </TabPanel>

        {/* Content Structure Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6">Content Structure</Typography>

            <TextField
              label="Preferred Content Formats"
              value={formData.contentStructure?.formats?.join(", ") || ""}
              onChange={(e) =>
                handleArrayInput("contentStructure", "formats", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Enter content formats separated by commas (e.g., Lists, Q&A, Stories, How-to guides)"
            />

            <TextField
              label="Content Organization Patterns"
              value={formData.contentStructure?.organization?.join(", ") || ""}
              onChange={(e) =>
                handleArrayInput("contentStructure", "organization", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Enter organization patterns separated by commas (e.g., Problem-solution, Chronological, Most-to-least important)"
            />

            <TextField
              label="Structural Preferences"
              value={formData.contentStructure?.preferences?.join(", ") || ""}
              onChange={(e) =>
                handleArrayInput("contentStructure", "preferences", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Enter structural preferences separated by commas (e.g., Short paragraphs, Bulleted lists, Section headers)"
            />
          </Box>
        </TabPanel>

        {/* Visual & Distinctive Tab */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6">Visual Elements</Typography>

            <TextField
              label="Brand Imagery Themes"
              value={formData.visualElements?.imagery?.join(", ") || ""}
              onChange={(e) => handleArrayInput("visualElements", "imagery", e)}
              fullWidth
              multiline
              rows={2}
              placeholder="Enter imagery themes separated by commas (e.g., Nature, Technology, People)"
            />

            <TextField
              label="Brand Colors"
              value={formData.visualElements?.colors?.join(", ") || ""}
              onChange={(e) => handleArrayInput("visualElements", "colors", e)}
              fullWidth
              multiline
              rows={2}
              placeholder="Enter brand colors separated by commas (e.g., Deep blue, Vibrant orange, Cool gray)"
            />

            <TextField
              label="Typography Style"
              value={formData.visualElements?.typography}
              onChange={(e) =>
                handleNestedChange(
                  "visualElements",
                  "typography",
                  e.target.value
                )
              }
              fullWidth
              placeholder="e.g., Clean sans-serif for headers, Serif for body text"
            />

            <Typography variant="h6" sx={{ mt: 2 }}>
              Distinctive Brand Features
            </Typography>

            <TextField
              label="Unique Brand Elements"
              value={
                formData.distinctiveFeatures?.unique_elements?.join(", ") || ""
              }
              onChange={(e) =>
                handleArrayInput("distinctiveFeatures", "unique_elements", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Enter unique elements separated by commas (e.g., Signature sign-off, Special formatting)"
            />

            <TextField
              label="Brand Taglines"
              value={formData.distinctiveFeatures?.taglines?.join(", ") || ""}
              onChange={(e) =>
                handleArrayInput("distinctiveFeatures", "taglines", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Enter brand taglines separated by commas"
            />

            <TextField
              label="Brand Motifs & Symbols"
              value={formData.distinctiveFeatures?.motifs?.join(", ") || ""}
              onChange={(e) =>
                handleArrayInput("distinctiveFeatures", "motifs", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Enter recurring motifs or symbols separated by commas"
            />
          </Box>
        </TabPanel>

        {/* Personal Narrative Tab */}
        <TabPanel value={tabValue} index={5}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              LinkedIn and social platforms favor personal, story-driven content over generic advice.
              Add your real experiences, wins, and lessons learned to create authentic content that resonates.
            </Typography>

            <Typography variant="h6">Personal Background</Typography>
            <Divider />

            <TextField
              label="Professional Bio"
              value={formData.personalBackground?.bio || ""}
              onChange={(e) =>
                handleNestedChange("personalBackground", "bio", e.target.value)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="A short professional bio (e.g., 'Former engineer turned founder. 3x startup founder. Dad of 2.')"
            />

            <TextField
              label="Origin Story"
              value={formData.personalBackground?.originStory || ""}
              onChange={(e) =>
                handleNestedChange("personalBackground", "originStory", e.target.value)
              }
              fullWidth
              multiline
              rows={3}
              placeholder="How did you get started? What's your founding story or career beginning? (e.g., 'Started coding in my parents' garage at 15...')"
            />

            <TextField
              label="Career Journey Milestones"
              value={formData.personalBackground?.careerJourney?.join(", ") || ""}
              onChange={(e) =>
                handleArrayInput("personalBackground", "careerJourney", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Key career milestones separated by commas (e.g., 'First job at Google, Founded XYZ in 2015, Acquired by ABC')"
            />

            <TextField
              label="Formative Experiences"
              value={formData.personalBackground?.formativeExperiences?.join(", ") || ""}
              onChange={(e) =>
                handleArrayInput("personalBackground", "formativeExperiences", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Experiences that shaped you separated by commas (e.g., 'Working in a family business, Living abroad for 5 years')"
            />

            <TextField
              label="Personal Values"
              value={formData.personalBackground?.personalValues?.join(", ") || ""}
              onChange={(e) =>
                handleArrayInput("personalBackground", "personalValues", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Core values separated by commas (e.g., 'Radical transparency, Work-life balance, Continuous learning')"
            />

            <Typography variant="h6" sx={{ mt: 3 }}>Achievements</Typography>
            <Typography variant="body2" color="text.secondary">
              Reference these when sharing wins and building credibility.
            </Typography>
            <Divider />

            <TextField
              label="Career Wins"
              value={formData.achievements?.careerWins?.join(", ") || ""}
              onChange={(e) =>
                handleArrayInput("achievements", "careerWins", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Major accomplishments separated by commas (e.g., 'Built team from 5 to 50, Launched product in 6 months')"
            />

            <TextField
              label="Metrics & Results"
              value={formData.achievements?.metrics?.join(", ") || ""}
              onChange={(e) =>
                handleArrayInput("achievements", "metrics", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Quantifiable results separated by commas (e.g., '$10M raised, 500K users, 300% revenue growth')"
            />

            <TextField
              label="Recognition & Awards"
              value={formData.achievements?.recognition?.join(", ") || ""}
              onChange={(e) =>
                handleArrayInput("achievements", "recognition", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Awards and honors separated by commas (e.g., 'Forbes 30 Under 30, Inc 5000, Best Startup 2023')"
            />

            <TextField
              label="Proud Moments"
              value={formData.achievements?.proudMoments?.join(", ") || ""}
              onChange={(e) =>
                handleArrayInput("achievements", "proudMoments", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Personal proud moments separated by commas (e.g., 'First hire became a CEO, Mentored 50+ founders')"
            />

            <Typography variant="h6" sx={{ mt: 3 }}>Failures & Lessons</Typography>
            <Typography variant="body2" color="text.secondary">
              Vulnerable content often gets the highest engagement. Share setbacks that taught you something.
            </Typography>
            <Divider />

            <TextField
              label="Setbacks"
              value={formData.failures?.setbacks?.join(", ") || ""}
              onChange={(e) =>
                handleArrayInput("failures", "setbacks", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Major setbacks or failures separated by commas (e.g., 'First startup failed, Got fired from dream job, Lost biggest client')"
            />

            <TextField
              label="Lessons Learned"
              value={formData.failures?.lessonsLearned?.join(", ") || ""}
              onChange={(e) =>
                handleArrayInput("failures", "lessonsLearned", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Key lessons from failures separated by commas (e.g., 'Cash flow is king, Hire slow fire fast, Customer feedback matters')"
            />

            <TextField
              label="Pivots"
              value={formData.failures?.pivots?.join(", ") || ""}
              onChange={(e) =>
                handleArrayInput("failures", "pivots", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Major pivots or changes separated by commas (e.g., 'Switched from B2C to B2B, Changed from services to product')"
            />

            <TextField
              label="Vulnerability Topics"
              value={formData.failures?.vulnerabilityTopics?.join(", ") || ""}
              onChange={(e) =>
                handleArrayInput("failures", "vulnerabilityTopics", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Topics you're comfortable being vulnerable about separated by commas (e.g., 'Imposter syndrome, Work-life balance struggles, Mental health')"
            />
          </Box>
        </TabPanel>

        {/* Editing Instructions Tab */}
        <TabPanel value={tabValue} index={6}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Provide custom instructions for the AI content editor. These instructions will be used when
              polishing and humanizing generated content to better match your brand voice.
            </Typography>

            <Typography variant="h6">Editing Instructions</Typography>
            <Divider />

            <TextField
              name="editingInstructions"
              label="Custom Editing Instructions"
              value={formData.editingInstructions || ""}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={12}
              placeholder={`Add specific editing guidelines for your content. Examples:

• Always use "we" instead of "I" when referring to the company
• Avoid exclamation marks - keep tone professional but warm
• Replace "utilize" with "use" and "leverage" with "apply"
• Keep sentences under 20 words when possible
• Always include a call-to-action at the end
• Use bullet points for lists of 3+ items
• Avoid industry jargon unless necessary
• Start paragraphs with action verbs
• Include data/statistics when making claims`}
              helperText={`${formData.editingInstructions?.length || 0}/${BRAND_VOICE_FIELD_LIMITS.editingInstructions} characters`}
              inputProps={{ maxLength: BRAND_VOICE_FIELD_LIMITS.editingInstructions }}
            />

            <Typography variant="caption" color="text.secondary">
              These instructions will be applied after content is generated to polish and refine
              the output according to your specific preferences. Be as specific as possible about
              word choices, formatting preferences, and stylistic guidelines.
            </Typography>
          </Box>
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <LoadingButton
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          loading={loading}
          disabled={!isFormValid}
        >
          {existingData ? "Update" : "Create"} Brand Voice
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
